import React, { useState, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import axios from 'axios';
import { Button } from '../../Button/button';
import './TabContent.css';
import AlertPopup from './AlertPopup';
import ConfirmationModal from './ConfirmationModal';

const LocationTab = ({ userDetails, onUserUpdate }) => {
    const [locations, setLocations] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ message: '', type: 'info' });
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, itemId: null, itemName: '' });

    useEffect(() => {
        if (userDetails && userDetails.id) {
            fetchUserLocations();
        }
    }, [userDetails]);

    const fetchUserLocations = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/${userDetails.id}/locations`);
            setLocations(response.data);
        } catch (error) {
            console.error('Error fetching locations:', error);
            setAlert({
                message: 'Failed to load locations. Please refresh the page.',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLocation = async (values, { resetForm }) => {
        try {
            const payload = {
                user_id: userDetails.id,
                address: values.address,
                details: values.details,
                city: values.city,
                province: values.province,
                country: values.country,
                postal_code: values.postal_code
            };

            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/users/${userDetails.id}/locations`, payload);
            setAlert({
                message: 'Location created successfully!',
                type: 'success'
            });
            setShowAddForm(false);
            resetForm();
            fetchUserLocations();
        } catch (error) {
            console.error('Error creating location:', error);
            setAlert({
                message: 'Failed to create location. Please try again.',
                type: 'error'
            });
        }
    };

    const handleUpdateLocation = async (locationId, values) => {
        try {
            const payload = {
                address: values.address,
                details: values.details,
                city: values.city,
                province: values.province,
                country: values.country,
                postal_code: values.postal_code
            };

            await axios.put(`${process.env.REACT_APP_API_BASE_URL}/locations/${locationId}`, payload);
            setAlert({
                message: 'Location updated successfully!',
                type: 'success'
            });
            setEditingLocation(null);
            fetchUserLocations();
        } catch (error) {
            console.error('Error updating location:', error);
            setAlert({
                message: 'Failed to update location. Please try again.',
                type: 'error'
            });
        }
    };

    const handleDeleteLocation = async (locationId) => {
        try {
            await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/locations/${locationId}`);
            fetchUserLocations();
        } catch (error) {
            console.error('Error deleting location:', error);
            setAlert({
                message: 'Failed to delete location. Please try again.',
                type: 'error'
            });
        }
    };

    const openDeleteModal = (locationId, locationName) => {
        setDeleteModal({
            isOpen: true,
            itemId: locationId,
            itemName: locationName
        });
    };

    const closeDeleteModal = () => {
        setDeleteModal({
            isOpen: false,
            itemId: null,
            itemName: ''
        });
    };

    const confirmDelete = () => {
        if (deleteModal.itemId) {
            handleDeleteLocation(deleteModal.itemId);
        }
    };

    if (!userDetails) {
        return (
            <div className="loaderContainer">
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <>
            <AlertPopup
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({ message: '', type: 'info' })}
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={closeDeleteModal}
                onConfirm={confirmDelete}
                title="Delete Location"
                message={`Are you sure you want to delete "${deleteModal.itemName}"? This action cannot be undone.`}
                confirmText="Delete Location"
                cancelText="Cancel"
                type="danger"
            />

            <div className="tab-content">
                <div className="tab-header">
                    <h2>Location Information</h2>
                    <p>Manage your locations and addresses</p>
                </div>

                {/* Add New Location Button */}
                <div className="mb-4">
                    <Button
                        type="button"
                        buttonStyle="btn--primary"
                        onClick={() => setShowAddForm(!showAddForm)}
                    >
                        {showAddForm ? 'Cancel' : 'Add New Location'}
                    </Button>
                </div>

                {/* Add New Location Form */}
                {showAddForm && (
                    <div className="form-box mb-4">
                        <h3>Add New Location</h3>
                        <Formik
                            initialValues={{
                                address: '',
                                details: '',
                                city: '',
                                province: '',
                                country: '',
                                postal_code: ''
                            }}
                            onSubmit={handleCreateLocation}
                        >
                            {({ values, resetForm }) => (
                                <Form>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Street Address *</label>
                                            <Field name="address" className="form-control" placeholder="Enter your street address" required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Details</label>
                                            <Field name="details" className="form-control" placeholder="Enter suite, unit, etc." required />
                                        </div>
                                    </div>

                                    <div className="row g-3 mt-3">
                                        <div className="col-md-6">
                                            <label className="form-label">City</label>
                                            <Field name="city" className="form-control" placeholder="City" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Province/State</label>
                                            <Field name="province" className="form-control" placeholder="Province or State" />
                                        </div>
                                    </div>

                                    <div className="row g-3 mt-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Country</label>
                                            <Field name="country" className="form-control" placeholder="Country" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Postal Code</label>
                                            <Field name="postal_code" className="form-control" placeholder="Postal Code" />
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end gap-3 mt-4">
                                        <Button
                                            type="button"
                                            buttonStyle="btn--secondary"
                                            onClick={() => {
                                                setShowAddForm(false);
                                                resetForm();
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button type="submit" buttonStyle="btn--primary">Create Location</Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                )}

                {/* Existing Locations */}
                <div className="locations-container">
                    <h3>Your Locations</h3>
                    {loading ? (
                        <div className="loaderContainer">
                            <div className="loader"></div>
                        </div>
                    ) : locations.length === 0 ? (
                        <div className="no-locations">
                            <p>No locations found. Add your first location above.</p>
                        </div>
                    ) : (
                        <div className="locations-grid">
                            {locations.map((location) => (
                                <div key={location.id} className="location-card">
                                    {editingLocation === location.id ? (
                                        <LocationEditForm
                                            location={location}
                                            onSave={(values) => handleUpdateLocation(location.id, values)}
                                            onCancel={() => setEditingLocation(null)}
                                        />
                                    ) : (
                                        <LocationDisplay
                                            location={location}
                                            onEdit={() => setEditingLocation(location.id)}
                                            onDelete={() => openDeleteModal(location.id, location.address)}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

// Component for displaying location information
const LocationDisplay = ({ location, onEdit, onDelete }) => (
    <div className="location-content">
        <div className="location-header">
            <h4>{location.address}</h4>
            <div className="location-actions">
                <Button
                    type="button"
                    buttonStyle="btn--secondary"
                    onClick={onEdit}
                >
                    Edit
                </Button>
                <Button
                    type="button"
                    buttonStyle="btn--danger"
                    onClick={onDelete}
                >
                    Delete
                </Button>
            </div>
        </div>
        <div className="location-details">
            {location.details && <p><strong>Details:</strong> {location.details}</p>}
            {location.city && <p><strong>City:</strong> {location.city}</p>}
            {location.province && <p><strong>Province:</strong> {location.province}</p>}
            {location.country && <p><strong>Country:</strong> {location.country}</p>}
            {location.postal_code && <p><strong>Postal Code:</strong> {location.postal_code}</p>}
            <p><strong>Coordinates:</strong> {location.lat}, {location.lng}</p>
        </div>
    </div>
);

// Component for editing location
const LocationEditForm = ({ location, onSave, onCancel }) => (
    <Formik
        initialValues={{
            address: location.address || '',
            details: location.details || '',
            city: location.city || '',
            province: location.province || '',
            country: location.country || '',
            postal_code: location.postal_code || ''
        }}
        onSubmit={onSave}
    >
        {({ values }) => (
            <Form>
                <div className="row g-3">
                    <div className="col-12">
                        <label className="form-label">Street Address *</label>
                        <Field name="address" className="form-control" placeholder="Enter your street address" required />
                    </div>
                    <div className="col-12">
                        <label className="form-label">Details</label>
                        <Field name="details" className="form-control" placeholder="Enter suite, unit, etc." required />
                    </div>
                </div>

                <div className="row g-3 mt-3">
                    <div className="col-md-6">
                        <label className="form-label">City</label>
                        <Field name="city" className="form-control" placeholder="City" />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Province/State</label>
                        <Field name="province" className="form-control" placeholder="Province or State" />
                    </div>
                </div>

                <div className="row g-3 mt-3">
                    <div className="col-md-6">
                        <label className="form-label">Country</label>
                        <Field name="country" className="form-control" placeholder="Country" />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Postal Code</label>
                        <Field name="postal_code" className="form-control" placeholder="Postal Code" />
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-3 mt-4">
                    <Button type="button" buttonStyle="btn--secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" buttonStyle="btn--primary">Save Changes</Button>
                </div>
            </Form>
        )}
    </Formik>
);

export default LocationTab; 